/**
 * The path the browser sees the app under, "/" or "/eva/", from the env value
 * Vite's `base` is set from. Read via process.env rather than import.meta.env,
 * which jest cannot parse. Always ends in "/".
 */
export function basePath(): string {
  const raw = process.env.REACT_APP_BASE_PATH || "/";
  return raw.endsWith("/") ? raw : `${raw}/`;
}

/** The base without its trailing slash: "/eva", or "" at the root. */
const baseRoot = (): string => basePath().slice(0, -1);

/** On the base or below it, by whole segment: "/eva/app" is, "/evaluations" is not. */
const isUnderRoot = (pathname: string, root: string): boolean =>
  pathname === root || pathname.startsWith(`${root}/`);

/**
 * A root-relative path under the app's base, for what Vite cannot rewrite:
 * string literals handed to non-DOM APIs and redirects that bypass the router.
 * Imports, `new URL(..., import.meta.url)` and index.html links need nothing.
 */
export function publicUrl(path: string): string {
  return `${basePath()}${path.replace(/^\/+/, "")}`;
}

/**
 * `window.location.pathname` as the router sees it, base removed; handing the
 * router a base-prefixed path would double the base.
 */
export function routerPath(pathname: string): string {
  const root = baseRoot();
  return root && isUnderRoot(pathname, root)
    ? pathname.slice(root.length) || "/"
    : pathname;
}

/**
 * Where to send the browser when it landed outside the base (a pre-move URL
 * such as eva.<apex>/app): the same path under it, query and hash intact.
 * Null when it is already there, or when there is no base.
 */
export function baseRedirectTarget(loc: {
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const root = baseRoot();
  if (!root || isUnderRoot(loc.pathname, root)) return null;
  return `${publicUrl(loc.pathname)}${loc.search}${loc.hash}`;
}
